import { CardConfig } from 'components/DynamicDashboard/interfaces';
import i18n from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import ApplicationState from 'store/interfaces';
import palette from 'theme/palette';
import {
  getBehaviorReportRequest,
  getProductReportRequest,
  getRegionReportRequest,
  getZeroTrustReportRequest,
  getMaintenanceReportRequest,
  getVirtualizerReportRequest,
  getStatusReportRequest,
  getProviderReportRequest,
  getDNSReportRequest,
  getProductTypeReportRequest,
  getConnectionTypeEnvironmentReportRequest,
} from 'modules/environments/actions';

const DEFAULT_SELECTED_CARDS = [
  'env_status',
  'zero_trust_enabled',
  'behavior_type',
];

const useEnvironmentsDashboardSelector = ({
  environments: {
    DNSReport,
    isLoadingDNSReport,
    behaviorReport,
    isLoadingBehaviorReport,
    connectionTypeEnvironmentReport,
    isLoadingConnectionTypeEnvironmentReport,
    isLoading,
    maintenanceReport,
    isLoadingMaintenanceReport,
    productReport,
    isLoadingProductReport,
    productTypeReport,
    isLoadingProductTypeReport,
    providerReport,
    isLoadingProviderReport,
    regionReport,
    isLoadingRegionReport,
    statusReport,
    isLoadingStatusReport,
    virtualizerReport,
    isLoadingVirtualizerReport,
    zeroTrustReport,
    isLoadingZeroTrustReport,
  },
}: ApplicationState) => ({
  DNSReport,
  behaviorReport,
  connectionTypeEnvironmentReport,
  isLoading:
    isLoading ||
    isLoadingDNSReport ||
    isLoadingBehaviorReport ||
    isLoadingConnectionTypeEnvironmentReport ||
    isLoadingMaintenanceReport ||
    isLoadingProductReport ||
    isLoadingProductTypeReport ||
    isLoadingProviderReport ||
    isLoadingRegionReport ||
    isLoadingStatusReport ||
    isLoadingVirtualizerReport ||
    isLoadingZeroTrustReport,
  maintenanceReport,
  productReport,
  productTypeReport,
  providerReport,
  regionReport,
  statusReport,
  virtualizerReport,
  zeroTrustReport,
});

interface Props {
  filters: Record<string, string>;
}

export const useEnvironmentsDashboard = ({ filters }: Props) => {
  const {
    DNSReport,
    behaviorReport,
    connectionTypeEnvironmentReport,
    isLoading,
    maintenanceReport,
    productReport,
    productTypeReport,
    providerReport,
    regionReport,
    statusReport,
    virtualizerReport,
    zeroTrustReport,
  } = useSelector(useEnvironmentsDashboardSelector);
  const dispatch = useDispatch();

  const cardsConfig: CardConfig[] = [
    {
      key: 'env_status',
      title: 'ENVIRONMENTS_STATUS_KEY',
      modalTitle: 'ENV_STATUS_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_STATUS_KEY',
      multiple: true,
      report: statusReport,
      getReport: () => dispatch(getStatusReportRequest({ filters })),
      options: [
        {
          field: 'development_environment_count',
          value: 'development',
          color: palette.digitalBlue,
          label: 'ENVIRONMENT_STATUS.DEVELOPMENT_KEY',
        },
        {
          field: 'deployment_environment_count',
          value: 'deployment',
          color: palette.digitalBlueLight1,
          label: 'ENVIRONMENT_STATUS.DEPLOYMENT_KEY',
        },
        {
          field: 'demonstration_environment_count',
          value: 'demonstration',
          color: palette.lightBlue,
          label: 'ENVIRONMENT_STATUS.DEMONSTRATION_KEY',
        },
        {
          field: 'homologation_environment_count',
          value: 'homologation',
          color: palette.deepBlue,
          label: 'ENVIRONMENT_STATUS.HOMOLOGATION_KEY',
        },
        {
          field: 'migration_environment_count',
          value: 'migration',
          color: palette.digitalBlueLight2,
          label: 'ENVIRONMENT_STATUS.MIGRATION_KEY',
        },
        {
          field: 'pending_environment_count',
          value: 'pending',
          color: palette.lightBlueLight3,
          label: 'ENVIRONMENT_STATUS.PENDING_KEY',
        },
        {
          field: 'production_environment_count',
          value: 'production',
          color: palette.deepBlueLight3,
          label: 'ENVIRONMENT_STATUS.PRODUCTION_KEY',
        },
      ],
    },
    {
      key: 'product',
      title: 'PRODUCT_KEY',
      modalTitle: 'ENV_PRODUCT_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_PRODUCT_KEY',
      report: productReport,
      getReport: () => dispatch(getProductReportRequest({ filters })),
      options: [
        {
          field: 'autosky',
          value: 'autosky',
          label: 'Autosky',
          color: palette.digitalBlue,
        },
        {
          field: 'business_one',
          value: 'business_one',
          label: 'SAP',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'product_type',
      title: 'PRODUCT_TYPE_KEY',
      modalTitle: 'ENV_PRODUCT_TYPE_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_PRODUCT_TYPE_KEY',
      multiple: true,
      report: productTypeReport,
      getReport: () => dispatch(getProductTypeReportRequest({ filters })),
      options: [
        {
          field: 'manual',
          value: 'manual',
          color: palette.lightBlue,
          label: i18n.t('TYPE_MANUAL_KEY'),
        },
        {
          field: 'period',
          value: 'period',
          color: palette.deepBlueLight1,
          label: i18n.t('TYPE_ROBOT_PERIOD_KEY'),
        },
        {
          field: 'spot_period',
          value: 'spot_period',
          color: palette.lightBlueLight2,
          label: i18n.t('TYPE_ROBOT_PERIOD_SPOT_KEY'),
        },
        {
          field: 'micro',
          value: 'micro',
          color: palette.digitalBlueLight3,
          label: i18n.t('TYPE_ROBOT_MICRO_KEY'),
        },
        {
          field: 'haas',
          value: 'haas',
          color: palette.digitalBlueDark2,
          label: i18n.t('TYPE_HAAS_KEY'),
        },
        {
          field: 'ccc',
          value: 'ccc',
          color: palette.digitalBlue,
          label: i18n.t('TYPE_CCC_KEY'),
        },
        {
          field: 'dedicated',
          value: 'dedicated',
          color: palette.deepBlue,
          label: i18n.t('TYPE_DEDICATED_KEY'),
        },
        {
          field: 'shared',
          value: 'shared',
          color: palette.lightBlueDark2,
          label: i18n.t('TYPE_SHARED_KEY'),
        },
      ],
    },
    {
      key: 'region',
      title: 'REGIONS_KEY',
      modalTitle: 'ENV_REGION_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_REGION_KEY',
      multiple: true,
      report: regionReport?.regions_count,
      getReport: () => dispatch(getRegionReportRequest({ filters })),
      options:
        regionReport?.regions_count &&
        Object.keys(regionReport.regions_count).map((region, index) => {
          const colors = [
            palette.lightBlue,
            palette.deepBlue,
            palette.digitalBlue,
            palette.lightBlueDark3,
            palette.deepBlueDark2,
            palette.digitalBlueLight2,
            palette.deepBlueLight3,
            palette.lightBlueLight3,
            palette.deepBlueDark1,
            palette.lightBlueLight1,
            palette.deepBlueDark4,
            palette.digitalBlueLight1,
            palette.digitalBlueDark3,
            palette.deepBlueLight1,
            palette.lightBlueDark4,
            palette.digitalBlueDark4,
            palette.digitalBlueLight3,
            palette.lightBlueLight2,
            palette.digitalBlueDark2,
            palette.lightBlueDark1,
            palette.deepBlueDark3,
            palette.digitalBlueDark1,
            palette.deepBlueLight2,
            palette.lightBlueDark2,
          ];

          const colorIndex = index % colors.length;

          const field = region || 'others';
          const label = region || 'OTHERS_KEY';

          return {
            field,
            value: region,
            color: colors[colorIndex],
            label,
          };
        }),
    },
    {
      key: 'behavior_type',
      title: 'BEHAVIOR_KEY',
      modalTitle: 'ENV_BEHAVIOR_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_BEHAVIOR_KEY',
      multiple: true,
      report: behaviorReport,
      getReport: () => dispatch(getBehaviorReportRequest({ filters })),
      options: [
        {
          field: 'manual',
          value: 'manual',
          color: palette.deepBlue,
          label: 'MANUAL_KEY',
        },
        {
          field: 'period',
          value: 'period',
          color: palette.lightBlue,
          label: 'ADVISOR_PAGE.PERIOD_KEY',
        },
        {
          field: 'spot_period',
          value: 'spot_period',
          color: palette.digitalBlue,
          label: 'ADVISOR_PAGE.SPOT_PERIOD_KEY',
        },
        {
          field: 'micro',
          value: 'micro',
          color: palette.digitalBlueLight3,
          label: 'MICRO_KEY',
        },
      ],
    },
    {
      key: 'zero_trust_enabled',
      title: 'LOGS_PAGE.FIELDS.ZERO_TRUST_KEY',
      modalTitle: 'ENV_ZERO_TRUST_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_ZERO_TRUST_KEY',
      infoText: 'ZERO_TRUST_CARD_INFO_TEXT_KEY',
      report: zeroTrustReport,
      getReport: () => dispatch(getZeroTrustReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'maintenance',
      title: 'MAINTENANCE_AND_WARNING_KEY',
      modalTitle: 'ENV_MAINTENANCE_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_STATUS_KEY',
      multiple: true,
      report: maintenanceReport,
      getReport: () => dispatch(getMaintenanceReportRequest({ filters })),
      options: [
        {
          field: 'in_maintenance',
          value: 'in_maintenance',
          color: palette.deepBlue,
          label: 'DYNAMIC_FILTER.IN_MAINTENANCE_KEY',
        },
        {
          field: 'future_maintenance',
          value: 'future_maintenance',
          color: palette.lightBlue,
          label: 'MAINTENANCE_SCHEDULED_KEY',
        },
        {
          field: 'display_warning',
          value: 'display_warning',
          color: palette.digitalBlue,
          label: 'SHOW_ALERT_KEY',
        },
      ],
    },
    {
      key: 'virtualizer',
      title: 'VIRTUALIZER_KEY',
      modalTitle: 'ENV_VIRTUALIZER_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_VIRTUALIZER_KEY',
      multiple: true,
      report: virtualizerReport,
      getReport: () => dispatch(getVirtualizerReportRequest({ filters })),
      options: [
        {
          field: 'connect',
          value: 'connect',
          color: palette.lightBlue,
          label: 'ConnectSky',
        },
        {
          field: 'connect_direct',
          value: 'connect_direct',
          color: palette.deepBlueLight1,
          label: 'ConnectSky (Direct Connect)',
        },
        {
          field: 'rdp',
          value: 'rdp',
          color: palette.lightBlueLight2,
          label: `Microsoft Remote Desktop (${i18n.t('FILE_KEY')})`,
        },
        {
          field: 'rdp_direct',
          value: 'rdp_direct',
          color: palette.digitalBlueLight3,
          label: 'Microsoft Remote Desktop (Direct Connect)',
        },
        {
          field: 'rdp_app',
          value: 'rdp_app',
          color: palette.digitalBlueDark2,
          label: 'Microsoft Remote Desktop (RemoteApp)',
        },
        {
          field: 'tsplus',
          value: 'tsplus',
          color: palette.digitalBlue,
          label: 'TSPlus (WebBrowser HTML5)',
        },
        {
          field: 'tsplus_remote_app',
          value: 'tsplus_remote_app',
          color: palette.deepBlue,
          label: 'TSPlus (RemoteApp)',
        },
        {
          field: 'tsplus_all',
          value: 'tsplus_all',
          color: palette.lightBlueDark2,
          label: 'TSPlus (WebBrowser HTML and RemoteApp)',
        },
        {
          field: 'aspw',
          value: 'aspw',
          color: palette.deepBlueDark2,
          label: 'ASPW (WebBrowser)',
        },
        {
          field: 'aspw_all',
          value: 'aspw_all',
          color: palette.digitalBlueLight1,
          label: 'ASPW (WebBrowser and RemoteApp)',
        },
      ],
    },
    {
      key: 'provider_type',
      title: 'PROVIDER_KEY',
      modalTitle: 'ENV_PROVIDER_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_PROVIDER_KEY',
      multiple: true,
      report: providerReport,
      getReport: () => dispatch(getProviderReportRequest({ filters })),
      options: [
        {
          field: 'aws',
          value: 'aws',
          label: 'AWS',
          color: palette.deepBlue,
        },
        {
          field: 'oci',
          value: 'oci',
          label: 'OCI',
          color: palette.lightBlue,
        },
        {
          field: 'multicloud',
          value: 'multicloud',
          label: 'Multicloud',
          color: palette.digitalBlue,
        },
      ],
    },
    {
      key: 'use_dns',
      title: 'DNS',
      modalTitle: 'ENV_DNS_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_DNS_KEY',
      report: DNSReport,
      getReport: () => dispatch(getDNSReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'connection_type',
      title: 'CONNECTION_TYPE_KEY',
      modalTitle: 'ENV_CONNECTION_TYPE_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_ENV_CONNECTION_TYPE_KEY',
      report: connectionTypeEnvironmentReport,
      getReport: () =>
        dispatch(getConnectionTypeEnvironmentReportRequest({ filters })),
      options: [
        {
          field: 'private',
          value: 'private',
          label: 'BULK_CLIENTS.PRIVATE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'public',
          value: 'public',
          label: 'BULK_CLIENTS.PUBLIC_KEY',
          color: palette.deepBlue,
        },
      ],
    },
  ];

  return {
    cardsConfig,
    defaultSelectedCards: DEFAULT_SELECTED_CARDS,
    isLoading,
  };
};
